<script setup lang="ts">
import { computed } from 'vue'
import { NBreadcrumb, NBreadcrumbItem } from 'naive-ui'
import { useRoute, useRouter } from 'vue-router'
import { SvgIcon } from '@/components/common'
import { formatBreadcrumbItem, generateBreadcrumb, isBreadcrumbActive } from '@/utils/breadcrumb'
import type { BreadcrumbItem } from '@/utils/breadcrumb'
import { useTheme } from '@/hooks/useTheme'
import { useAppStore } from '@/store'
import { useWorkspaceFolderStore } from '@/store/modules/folder'
import { t } from '@/locales'
import { getRouteConfig, getRouteIcon } from '@/config/routes'

interface Props {
  separator?: string
  maxItems?: number
}

const props = withDefaults(defineProps<Props>(), {
  separator: '/',
  maxItems: 5,
})

const route = useRoute()
const router = useRouter()
const { isDark } = useTheme()
const workspaceFolderStore = useWorkspaceFolderStore()

// Get primary color from theme
const primaryColor = computed(() => {
  const appStore = useAppStore()
  return appStore.colorTheme || '#3b82f6'
})

// Helper function to get folder name by ID
const getFolderName = (folderId: string | number): string => {
  const folders = workspaceFolderStore.getFolders
  const folder = folders.find((f: any) => f.id === Number(folderId))
  return folder?.name || `Folder ${folderId}`
}

const breadcrumbItems = computed(() => {
  const items = generateBreadcrumb(route, router)

  // Process items to use config-based titles and icons
  const processedItems = items.map((item) => {
    const routeName = item.name

    if (routeName) {
      const config = getRouteConfig(routeName)

      if (config) {
        // Special handling for folder routes - use actual folder name
        if (routeName === 'folder' && route.params.folderId) {
          const folderId = Array.isArray(route.params.folderId)
            ? route.params.folderId[0]
            : route.params.folderId
          item.title = getFolderName(folderId)
          item.icon = 'ri:folder-line'
        }
        else {
          // Use i18n key from config for other routes
          const i18nKey = config.meta.breadcrumb?.i18nKey || config.meta.i18nKey
          if (i18nKey)
            item.title = t(i18nKey)

          // Use icon from config
          const icon = config.meta.breadcrumb?.icon || config.meta.icon
          if (icon)
            item.icon = icon
        }
      }
    }

    return item
  })

  // Always ensure "Trang chủ" is at the beginning
  const homeItem = {
    title: t('menu.home'),
    name: 'home',
    path: '/',
    icon: 'ri:home-line',
  }

  // If first item is not home, add it
  if (processedItems.length === 0 || processedItems[0]?.name !== 'home')
    processedItems.unshift(homeItem)

  // Limit number of items
  if (processedItems.length > props.maxItems) {
    return [
      processedItems[0], // Always keep home
      { title: '...', name: 'ellipsis' },
      ...processedItems.slice(-2),
    ]
  }

  return processedItems.map((item, index) =>
    formatBreadcrumbItem(item, index === processedItems.length - 1),
  )
})

const getItemIcon = (item: BreadcrumbItem): string => {
  // Use icon from processed item or get from config
  if (item.icon)
    return item.icon

  // Fallback to config-based icon
  if (item.name)
    return getRouteIcon(item.name)

  return 'ri:file-line'
}

const handleBreadcrumbClick = (item: BreadcrumbItem) => {
  if (item.name === 'ellipsis' || isBreadcrumbActive(item, route))
    return

  if (item.path)
    router.push(item.path)
  else if (item.name)
    router.push({ name: item.name, params: item.params })
}
</script>

<template>
  <NBreadcrumb :separator="separator" class="breadcrumb-container">
    <NBreadcrumbItem
      v-for="(item, index) in breadcrumbItems"
      :key="item.name || index"
      class="breadcrumb-item" :class="{
        'breadcrumb-item--active': isBreadcrumbActive(item, route),
        'breadcrumb-item--clickable': !isBreadcrumbActive(item, route) && item.name !== 'ellipsis',
        'breadcrumb-item--ellipsis': item.name === 'ellipsis',
      }"
      @click="handleBreadcrumbClick(item)"
    >
      <div class="breadcrumb-content">
        <!-- Dynamic icon from config -->
        <SvgIcon
          v-if="item.name !== 'ellipsis'"
          :icon="getItemIcon(item)"
          class="breadcrumb-icon"
          size="16"
          :style="{
            color: isBreadcrumbActive(item, route) ? primaryColor : undefined,
          }"
        />

        <span
          class="breadcrumb-title"
          :style="{
            color: isBreadcrumbActive(item, route) ? primaryColor : undefined,
          }"
        >
          {{ item.title }}
        </span>
      </div>
    </NBreadcrumbItem>
  </NBreadcrumb>
</template>

<style scoped>
.breadcrumb-container {
  @apply flex items-center text-sm;
}

.breadcrumb-item {
  @apply transition-all duration-200;
}

.breadcrumb-item--clickable {
  @apply cursor-pointer;
}

.breadcrumb-item--clickable:hover .breadcrumb-content {
  @apply transform scale-105;
}

.breadcrumb-item--clickable:hover .breadcrumb-icon,
.breadcrumb-item--clickable:hover .breadcrumb-title {
  color: v-bind(primaryColor) !important;
}

.breadcrumb-item--active {
  @apply font-medium;
}

.breadcrumb-item--active .breadcrumb-title {
  @apply font-semibold;
}

.breadcrumb-item--ellipsis {
  @apply text-gray-400 dark:text-gray-500 cursor-default;
}

.breadcrumb-content {
  @apply flex items-center gap-1.5 transition-all duration-200;
}

.breadcrumb-icon {
  @apply text-gray-500 dark:text-gray-400 transition-colors duration-200;
}

.breadcrumb-title {
  @apply truncate max-w-32 sm:max-w-48 transition-colors duration-200;
  color: inherit;
}

/* Responsive design */
@media (max-width: 640px) {
  .breadcrumb-container {
    @apply text-xs;
  }

  .breadcrumb-title {
    @apply max-w-20;
  }
}

/* Dark mode enhancements */
.dark .breadcrumb-item--clickable:hover .breadcrumb-icon,
.dark .breadcrumb-item--clickable:hover .breadcrumb-title {
  color: v-bind(primaryColor) !important;
}

/* Animation for breadcrumb changes */
.breadcrumb-item {
  @apply opacity-0 animate-fade-in;
  animation-delay: calc(var(--index, 0) * 0.1s);
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}
</style>
